import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dayIdLog } from '../lib/dayIdLog';
import { accountGeneration } from '../lib/accountStorage';
import { markDoseGroup, type GroupMarkResult } from '../lib/doseGroupMark';
import { fetchUserPreferences } from '../lib/userPreferences';
import { createPreferenceSync } from '../lib/preferenceSync';
import { onSignOut } from '../lib/signOutReset';
import {
  type Supplement,
  type SupplementDay,
  DEFAULT_SUPPLEMENT_ICON,
  sumSupplementXP,
  supplementStreak,
  adherencePct,
} from '../utils/supplements';
import { toLocalYMD, shiftYMD } from '../utils/hydration';
import { amountOn, countedDoses, extraKey, keyOfItem, markOf, mergeIntakeRecord, pauseItem, resumeItem, SKIP_PREFIX, type Dose, type DoseMark } from '../utils/intakeSchedule';
import { useSupplementStore, supplySince, loadRecordStart, recordStartOn } from './supplementStore';

// Medicine tracker — the Health lens twin of the Supplements tracker, on the SAME intake engine
// (utils/intakeSchedule — owner: "one engine for Supplements and Medications"): due doses from
// each medicine's schedule, Taken / Skip marks, one-off doses, supply. Its own
// `medication_logs` table + `medication_stack` prefs slice, and it feeds the HEALTH area rank
// (see useDisciplines) rather than Nourishment. Medications are personal, so the default list
// is EMPTY (no starter stack).
// ⚠️ The person's day (wake, meals, bed) is ONE setting, Supplements' `dayTimes`
// (user_preferences.intake_day_times) — read from there, never kept twice.
// A list saved before the engine (`{ id, name, dose, icon, slot, active }`) is one dose a day
// in its slot, keyed by its id — exactly as before.

const HISTORY_DAYS = 60;
/** How far back the record is read at most — a supply counted from longer ago reads "unknown". */
const SUPPLY_HISTORY_DAYS = 366;

/** Medications reuse the generic intake-item shape. */
export type Medication = Supplement;
export type MedicationDay = SupplementDay;

interface MedicationRow {
  log_date: string;
  taken_ids: string[] | null;
  scheduled: number | null;
}

function rowToDay(row: MedicationRow): MedicationDay {
  return { date: row.log_date, takenIds: Array.isArray(row.taken_ids) ? row.taken_ids : [], scheduled: row.scheduled ?? 0 };
}

interface MedicationStore {
  stack: Medication[];
  todayDate: string;
  todayTakenIds: string[];
  /** Doses due today (the schedule's count — `countedDoses` — stored with each change). */
  todayScheduled: number;
  history: MedicationDay[];
  /** The earliest day the record was read from — a supply counted from before it is unknown. */
  historyFrom: string;
  /**
   * ⭐ Where the record starts (`loadRecordStart`): the earliest saved day with something due and
   * marked, else today — `historyFrom` when the record began before the days loaded. A day before
   * it is BEFORE the record — never "not recorded", never counted.
   */
  recordStart: string;

  isLoading: boolean;
  /** The last load failed (Growth Foundations says "couldn't check" and offers Retry). */
  loadFailed: boolean;
  isInitialized: boolean;
  initializedUserId: string | null;

  initialize: () => Promise<void>;
  /** Re-read today's row (the tab came back — lib/dayLogFocusRefresh). Read only. */
  refreshToday: () => Promise<void>;
  toggleTaken: (id: string) => Promise<void>;
  /** Mark one of today's doses taken, skipped, or neither (utils/intakeSchedule). True when saved. */
  markDose: (key: string, mark: DoseMark) => Promise<boolean>;
  /** Log a one-off dose of a medicine today (it counts for supply, not adherence). True when saved. */
  logExtra: (itemId: string) => Promise<boolean>;
  /**
   * "✓ All N taken": each still-open dose of `keys` marked taken, one after another, the record read
   * again before each (lib/doseGroupMark). No habit — Medicine isn't linked.
   */
  markGroup: (keys: string[]) => Promise<GroupMarkResult>;
  /** "Restocked": the count on hand as of today. */
  restock: (id: string, count: number) => void;
  /** Add a medicine; returns its new id. */
  addMedication: (m: Omit<Medication, 'id' | 'active'>) => string;
  updateMedication: (id: string, patch: Partial<Omit<Medication, 'id'>>) => void;
  deleteMedication: (id: string) => void;
  toggleActive: (id: string) => void;

  getMedicationXP: () => number;
  getStreak: () => number;
  getTodayAdherence: () => { taken: number; scheduled: number; pct: number };
  /** Today's counted doses (`countedDoses`: the due ones, and one marked before its item was paused), in the day's order. */
  getTodayDoses: () => Dose[];
  clearStore: () => void;
}

/** The person's day — Supplements' setting (one for both). */
const dayTimes = () => useSupplementStore.getState().dayTimes;
/**
 * How many doses `date` counts with the record `ids` — what the day's record stores as `scheduled`:
 * the doses due, and one marked before its item was paused that day (`countedDoses`, as Supplements').
 */
const dueCount = (stack: Medication[], date: string, ids: readonly string[]): number => countedDoses(stack, date, ids, dayTimes()).length;

function withTodayInHistory(history: MedicationDay[], today: MedicationDay): MedicationDay[] {
  const others = history.filter(d => d.date !== today.date);
  return [...others, today].sort((a, b) => a.date.localeCompare(b.date));
}

/** Today's taken_ids: one id on or off in the SERVER's list (lib/dayIdLog). */
const dayLog = dayIdLog({
  table: 'medication_logs',
  column: 'taken_ids',
  what: 'medication',
  scheduled: (ids) => dueCount(useMedicationStore.getState().stack, toLocalYMD(new Date()), ids),
  show: (date, ids) => {
    const s = useMedicationStore.getState();
    const scheduled = dueCount(s.stack, date, ids);
    useMedicationStore.setState({ todayDate: date, todayTakenIds: ids, todayScheduled: scheduled, history: withTodayInHistory(s.history, { date, takenIds: ids, scheduled }), recordStart: recordStartOn(s, date) });
  },
  loadedFor: () => useMedicationStore.getState().initializedUserId,
});

/**
 * The list is `user_preferences.medication_stack`, merged per medication on every
 * write (lib/preferenceSync.ts) — a stale tab can no longer put back one deleted
 * elsewhere or drop one added there.
 */
const stackSync = createPreferenceSync<Medication[]>({
  column: 'medication_stack',
  // ⭐ a record changed on both sides keeps this tab's copy — its PAUSES merged (`mergeIntakeRecord`): a
  // stale tab never moves a pause's start or undoes a Resume it never saw (polish round, fix 1)
  rule: { records: 'id', of: mergeIntakeRecord },
  decode: (saved) => (Array.isArray(saved) ? (saved as Medication[]) : []),
  read: () => useMedicationStore.getState().stack,
  adopt: (stack) => applyStack(useMedicationStore.setState, useMedicationStore.getState, stack),
  label: 'medication list',
  loadedFor: () => useMedicationStore.getState().initializedUserId,
});

/** Await the pending list write (tests). */
export const flushMedicationStack = (): Promise<void> => stackSync.flush();

export const useMedicationStore = create<MedicationStore>((set, get) => ({
  stack: [],
  todayDate: toLocalYMD(new Date()),
  todayTakenIds: [],
  todayScheduled: 0,
  history: [],
  historyFrom: toLocalYMD(new Date()),
  recordStart: toLocalYMD(new Date()),

  isLoading: false,
  loadFailed: false,
  isInitialized: false,
  initializedUserId: null,

  initialize: () => dayLog.load(async () => {
    const at = dayLog.generation();
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== dayLog.generation()) return;
    if (get().initializedUserId === user.id) return;

    set({ isLoading: true, loadFailed: false });
    try {
      const prefs = await fetchUserPreferences();
      // ⚠️ A failed read is not an empty list: the load fails (as Hydration's does) — never "nothing saved".
      if (!prefs || prefs.failed) throw new Error('Could not read the medicine list');

      const todayStr = toLocalYMD(new Date());
      // Back far enough to count each supply from the day it was counted (a year at most).
      const since = supplySince(prefs?.row?.medication_stack, shiftYMD(todayStr, -HISTORY_DAYS), shiftYMD(todayStr, -SUPPLY_HISTORY_DAYS));
      const { data, error } = await supabase
        .from('medication_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', since)
        .order('log_date', { ascending: true });
      // Signed out while reading: the next account must not get this one's ticks — nor its
      // list as the base (so the list is loaded only after this).
      if (at !== dayLog.generation()) return;
      if (error) throw error;
      const rows = (data ?? []) as MedicationRow[];
      const recordStart = await loadRecordStart('medication_logs', user.id, rows, since, todayStr);
      if (at !== dayLog.generation()) return;
      const stack = stackSync.load(prefs?.row?.medication_stack ?? null, prefs?.row?.updated_at);

      const history = rows.map(rowToDay);
      const todayRow = rows.find(r => r.log_date === todayStr);
      const todayTakenIds = Array.isArray(todayRow?.taken_ids) ? (todayRow!.taken_ids as string[]) : [];
      // Today's due count follows the CURRENT list (today is live).
      const todayScheduled = dueCount(stack, todayStr, todayTakenIds);

      set({
        stack,
        todayDate: todayStr,
        todayTakenIds,
        todayScheduled,
        history: withTodayInHistory(history, { date: todayStr, takenIds: todayTakenIds, scheduled: todayScheduled }),
        historyFrom: since,
        recordStart,
        isLoading: false,
        loadFailed: false,
        isInitialized: true,
        initializedUserId: user.id,
      });
      dayLog.loaded(todayStr, todayTakenIds);
    } catch (err) {
      if (at !== dayLog.generation()) return; // the next account's load may be running: leave its flag and base alone
      console.error('Error initializing medications:', err);
      stackSync.reset(); // the list was never adopted — its base must not stand either
      set({ isLoading: false, loadFailed: true });
    }
  }),

  refreshToday: () => dayLog.refresh(),

  // ⚠️ Flips what THIS screen shows — applied to the server's list, so a stale screen can
  // only ever set the one id the user tapped (never re-send or drop the others).
  toggleTaken: async (id: string) => {
    await dayLog.set(id, !get().todayTakenIds.includes(id));
  },

  markDose: (key, mark) => dayLog.mark(key, mark),

  // ⚠️ The key is chosen against what THIS screen shows; two one-offs of the same medicine from two
  // stale tabs could share a key — the second is then a no-op (one dose recorded, never two).
  logExtra: (itemId) => dayLog.mark(extraKey(get().todayTakenIds, itemId), 'taken'),

  // ⚠️ One account: it stops at the first dose after a sign-out (or another account in).
  markGroup: (keys) => {
    const at = dayLog.generation(), account = accountGeneration();
    return markDoseGroup(keys, { read: get, stillHere: () => at === dayLog.generation() && account === accountGeneration(), noun: 'medicines' });
  },

  // ⚠️ A count is "on hand before that day's doses" (utils/intakeSchedule): what was taken today
  // is added back, so "I have 60 now" after the morning dose still reads 60 — not 59.
  restock: (id, count) => {
    const { stack, todayDate, todayTakenIds } = get();
    const item = stack.find(m => m.id === id);
    if (!item) return;
    const takenToday = todayTakenIds.filter(k => !k.startsWith(SKIP_PREFIX) && keyOfItem(k, id)).length * amountOn(item, todayDate);
    const n = Math.max(0, Math.round(count)) + takenToday;
    persistStack(set, get, stack.map(m => (m.id === id ? { ...m, supply: { count: n, asOf: todayDate } } : m)));
  },

  addMedication: (m) => {
    const full: Medication = {
      ...m,
      id: crypto.randomUUID(),
      name: m.name.trim() || 'Medication',
      dose: m.dose.trim(),
      icon: m.icon || DEFAULT_SUPPLEMENT_ICON,
      slot: m.slot,
      active: true,
    };
    persistStack(set, get, [...get().stack, full]);
    return full.id;
  },

  updateMedication: (id, patch) => {
    persistStack(set, get, get().stack.map(m => (m.id === id ? { ...m, ...patch } : m)));
  },

  deleteMedication: (id) => {
    persistStack(set, get, get().stack.filter(m => m.id !== id));
  },

  // ⭐ Pause and Resume keep their dates, as Supplements' do (utils/intakeSchedule `pauses`).
  toggleActive: (id) => {
    const today = get().todayDate;
    persistStack(set, get, get().stack.map(m => (m.id !== id ? m : m.active ? pauseItem(m, today) : resumeItem(m, today))));
  },

  getMedicationXP: () => sumSupplementXP(get().history),
  // A day with nothing due (and no record) is passed over, not a break.
  getStreak: () => supplementStreak(get().history, get().todayDate, shiftYMD, date => dueCount(get().stack, date, [])),
  // Only the marks on today's counted doses count: a mark left on a dose since removed doesn't.
  getTodayAdherence: () => {
    const { todayTakenIds, todayScheduled } = get();
    const taken = get().getTodayDoses().filter(d => markOf(todayTakenIds, d.key) === 'taken').length;
    return { taken, scheduled: todayScheduled, pct: adherencePct(taken, todayScheduled) };
  },
  getTodayDoses: () => countedDoses(get().stack, get().todayDate, get().todayTakenIds, dayTimes()),

  clearStore: () => {
    dayLog.clear();
    stackSync.reset();
    set({
      stack: [],
      todayDate: toLocalYMD(new Date()),
      todayTakenIds: [],
      todayScheduled: 0,
      history: [],
      historyFrom: toLocalYMD(new Date()),
      recordStart: toLocalYMD(new Date()),
      isLoading: false,
      loadFailed: false,
      isInitialized: false,
      initializedUserId: null,
    });
  },
}));

// The list + a refreshed today-scheduled, in one set.
function applyStack(
  set: (partial: Partial<MedicationStore>) => void,
  get: () => MedicationStore,
  stack: Medication[]
) {
  const state = get();
  const scheduled = dueCount(stack, state.todayDate, state.todayTakenIds);
  const todayDay: MedicationDay = { date: state.todayDate, takenIds: state.todayTakenIds, scheduled };
  set({ stack, todayScheduled: scheduled, history: withTodayInHistory(state.history, todayDay) });
}

// Optimistic local set of the list, then persist (merged into the cloud's copy).
function persistStack(
  set: (partial: Partial<MedicationStore>) => void,
  get: () => MedicationStore,
  stack: Medication[]
) {
  const was = get().todayScheduled;
  stackSync.recordBase(get().stack);
  applyStack(set, get, stack);
  stackSync.schedule();
  // ⭐ Today's count moved (a Pause, a Resume…): today's row is recounted too, as Supplements' (polish round, fix 1)
  if (get().todayScheduled !== was) void dayLog.rescore();
}

// The person's day moved (Your day saved, or loaded after this list): today's due count follows
// it. Before this list has loaded there is nothing to recount (its load reads the day then).
// ⚠️ Today's ROW is recounted only for a day SAVED (both stores loaded before and after) — never
// for a load: a load never writes.
useSupplementStore.subscribe((s, prev) => {
  const meds = useMedicationStore.getState();
  if (s.dayTimes === prev.dayTimes || !meds.isInitialized) return;
  const was = meds.todayScheduled;
  applyStack(useMedicationStore.setState, useMedicationStore.getState, meds.stack);
  if (s.isInitialized && prev.isInitialized && useMedicationStore.getState().todayScheduled !== was) void dayLog.rescore();
});

onSignOut(() => useMedicationStore.getState().clearStore());
